import { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import { useAuthContext } from "../../context/auth";
import { crearMovimiento } from "../../utils/flujoCaja";
import { actualizarMatch, confirmarMovimiento } from "../../utils/movimientosBancarios";
import { parsearOnSignDesdeTexto } from "../../utils/parseOnSignPdf";
import { extraerTextoPdf } from "../../utils/pdf/extraerTextoPdf";
import { parsearAccuweatherDesdeTexto } from "../../utils/pdf/parsers/accuweather";
import { parsearAnthropicDesdeTexto } from "../../utils/pdf/parsers/anthropic";
import { parsearCanvaDesdeTexto } from "../../utils/pdf/parsers/canva";
import { parsearDropboxDesdeTexto } from "../../utils/pdf/parsers/dropbox";
import { parsearGoogleWorkspaceDesdeTexto } from "../../utils/pdf/parsers/googleWorkspace";
import { parsearLatamDesdeTexto } from "../../utils/pdf/parsers/latam";
import { parsearScreenfeedDesdeTexto } from "../../utils/pdf/parsers/screenfeed";
import { parsearSendgridDesdeTexto } from "../../utils/pdf/parsers/sendgrid";
import { parsearVedicRishiDesdeTexto } from "../../utils/pdf/parsers/vedicRishi";
import { crearProveedor } from "../../utils/proveedores";
import { client } from "../../utils/supabase";
import ProveedorSelector from "../VincularMovimientoModal/ProveedorSelector";

// Registro de lectores de PDF por proveedor_id (regla 10.7 — NO comparar por nombre). El parser de
// cada proveedor extranjero devuelve el shape transversal { folio, fecha, periodo, concepto, monto,
// moneda, unidades }. La lectura automática solo se ofrece para los ids acá registrados. Cada entry:
//   parsear: (texto, nombreArchivo) => shape  (nombreArchivo lo usa Screenfeed para su folio)
//   nombre:  prefijo del concepto por molde
//   uni:     etiquetas singular/plural de unidades, o null si el proveedor NO factura por conteo
const REGISTRO_PARSERS = {
  18: { parsear: parsearOnSignDesdeTexto, nombre: "OnSign", uni: { s: "licencia", p: "licencias" } },
  10: { parsear: parsearGoogleWorkspaceDesdeTexto, nombre: "Google Workspace", uni: { s: "usuario", p: "usuarios" } },
  15: { parsear: parsearAccuweatherDesdeTexto, nombre: "AccuWeather", uni: null },
  51: { parsear: parsearAnthropicDesdeTexto, nombre: "Claude", uni: { s: "licencia", p: "licencias" } },
  75: { parsear: parsearCanvaDesdeTexto, nombre: "Canva", uni: { s: "licencia", p: "licencias" } },
  90: { parsear: parsearScreenfeedDesdeTexto, nombre: "Screenfeed", uni: { s: "unidad", p: "unidades" } },
  141: { parsear: parsearSendgridDesdeTexto, nombre: "SendGrid", uni: null },
  50: { parsear: parsearDropboxDesdeTexto, nombre: "Dropbox", uni: null },
  7: { parsear: parsearLatamDesdeTexto, nombre: "LATAM", uni: null },
  174: { parsear: parsearVedicRishiDesdeTexto, nombre: "Vedic Rishi", uni: null },
};

// ─── Helpers ─────────────────────────────────────────────────
const subirArchivoFactura = async (file) => {
  const ext = file.name.split(".").pop();
  const path = `externas/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await client.storage.from("facturas").upload(path, file);
  if (error) throw error;
  const { data } = client.storage.from("facturas").getPublicUrl(path);
  return data.publicUrl;
};

// Date del primer día del mes de `fechaStr` (YYYY-MM-DD). Parseo manual evita el bug
// de TZ que aparece con `new Date("YYYY-MM-DD")` en husos negativos (Chile/Perú).
const defaultPeriodoMes = (fechaStr) => {
  if (!fechaStr) {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }
  const [y, m] = fechaStr.split("-").map(Number);
  return new Date(y, m - 1, 1);
};

// Serializa la Date del periodo a "YYYY-MM-01" sin pasar por toISOString — que en
// husos negativos retorna el último día del mes anterior.
const periodoMesToString = (date) => {
  if (!date) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
};

const MESES_LABELS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

// ─── Hook con toda la lógica de form ────────────────────────
// Centraliza state + validación + submit. Permite que el padre (VincularMov)
// dispare submit desde su propio botón sin duplicar código.
export const useFacturaExternaForm = ({
  pais,
  moneda: monedaInitial,
  monto: montoInitial,
  proveedorId: proveedorIdLocked,
  proveedores,
  movimiento,
  onProveedorCreado,
  onFacturaCreada,
  onSuccess,
  onClose,
}) => {
  const { id: usuarioId } = useAuthContext();
  const isModoBancario = !!movimiento;

  const [proveedorId, setProveedorId] = useState(proveedorIdLocked || null);
  const [modoNuevoProv, setModoNuevoProv] = useState(false);
  const [nuevoProvNombre, setNuevoProvNombre] = useState("");
  const [nuevoProvRut, setNuevoProvRut] = useState("");

  const [extFecha, setExtFecha] = useState(movimiento?.fecha || new Date().toISOString().split("T")[0]);
  const [extPeriodoMes, setExtPeriodoMes] = useState(() =>
    defaultPeriodoMes(movimiento?.fecha || new Date().toISOString().split("T")[0])
  );
  const [extNumeroDoc, setExtNumeroDoc] = useState("");
  const [extConcepto, setExtConcepto] = useState("");
  const [extCuentaER, setExtCuentaER] = useState("");
  const [extArchivo, setExtArchivo] = useState(null);
  const [extMonto, setExtMonto] = useState(montoInitial != null ? String(montoInitial) : "");
  const [extMoneda, setExtMoneda] = useState(monedaInitial || "USD");
  // Cantidad de unidades/licencias/pantallas que factura el proveedor (ej. 187 en OnSign). Opcional
  // (nullable): solo aplica a proveedores que facturan por conteo. Se pre-llena al leer el PDF.
  const [extLicencias, setExtLicencias] = useState("");
  // Moneda leída del PDF por el parser. En modo bancario el monto/moneda de la factura se espejan del
  // banco (no del PDF), pero igual se guarda la del parser acá para el guard P4 (regla 9.41): bloquear
  // "Crear nueva" cuando la factura viene en otra moneda que el movimiento bancario. En standalone no
  // se usa (la moneda sale del propio parser vía extMoneda). null si no hubo lectura o falló.
  const [monedaParser, setMonedaParser] = useState(null);
  // Estado de la lectura del PDF para proveedores CON lector registrado (guard P5, regla 9.41, opción
  // A dura + A1). 'idle' = todavía no se intentó leer (o proveedor sin lector) → no bloquea (arranque
  // limpio); 'ok' = parseo exitoso → no bloquea; 'error' = el parser lanzó / PDF corrupto / se subió un
  // NO-PDF a un proveedor con lector (A1: lector obliga PDF) → BLOQUEA el guardado, sin escape manual.
  const [parseStatus, setParseStatus] = useState("idle");

  const [loading, setLoading] = useState(false);
  const [parseandoPdf, setParseandoPdf] = useState(false);

  const proveedorIdFinal = proveedorIdLocked || proveedorId;
  const proveedorLockedNombre = useMemo(
    () => (proveedorIdLocked ? proveedores.find((p) => p.id === proveedorIdLocked)?.nombre : null),
    [proveedorIdLocked, proveedores]
  );

  // El campo "Período" solo aplica a proveedores recurrentes (suscripciones, servicios mensuales).
  // Para compras puntuales (ej: un pasaje LATAM) no tiene sentido — se oculta y periodo_mes va NULL.
  // Proveedores nuevos creados desde este form no son recurrentes hasta editarlos, así que false.
  const esRecurrente = useMemo(
    () => !!proveedores.find((p) => p.id === proveedorIdFinal)?.es_recurrente,
    [proveedores, proveedorIdFinal]
  );

  // ¿El proveedor seleccionado tiene lector de PDF registrado? (por id, no por nombre — regla 10.7).
  // Habilita la lectura automática con el parser del proveedor.
  const configLector = REGISTRO_PARSERS[Number(proveedorIdFinal)] || null;
  const esLectorPdf = !!configLector;

  const montoFinal = isModoBancario ? Number(movimiento.monto || 0) : Number(extMonto || 0);
  const monedaFinal = isModoBancario ? monedaInitial : extMoneda;

  // P4 (regla 9.41 / 10.44): en modo bancario "Crear nueva" la moneda de la factura se espeja del
  // banco (monedaFinal = monedaInitial). Si el PDF viene en OTRA moneda, guardarla como la del banco
  // perdería la naturaleza real de la factura (ej. PDF USD contra débito CLP → factura CLP, mal). Se
  // bloquea. El cross-currency legítimo (factura USD → vincular a pago CLP, tolerancia 15% 10.44) va
  // por standalone + "Vincular existente", NO por este camino. En standalone monedaParser no aplica.
  const monedaMismatchBancario = isModoBancario && !!monedaParser && monedaParser !== monedaInitial;

  // Auto-preselect por descripción cruda del banco — solo modo bancario y solo si no hay
  // proveedor ya seleccionado/lockeado.
  useEffect(() => {
    if (!isModoBancario || proveedorIdLocked || proveedorId || proveedores.length === 0) return;
    const desc = (movimiento?.descripcion_raw || "").toLowerCase();
    if (!desc) return;
    const match = proveedores
      .filter((p) => p.es_extranjero)
      .find((p) => {
        const candidatos = [p.nombre, p.alias].filter(Boolean).map((s) => s.toLowerCase());
        return candidatos.some((c) => desc.includes(c));
      });
    if (match) {
      setProveedorId(match.id);
      if (match.cuenta_er_default && !extCuentaER) setExtCuentaER(match.cuenta_er_default);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModoBancario, proveedores, movimiento?.descripcion_raw]);

  // P5 (regla 9.41, opción A + A1): para un proveedor CON lector, la carga a mano es basura si el PDF
  // no se pudo leer (fecha/período/folio quedan en defaults peligrosos). Si el parser falló, el PDF es
  // corrupto, o se subió un no-PDF (A1) → parseStatus='error' → se bloquea el guardado sin escape. No
  // depende de isModoBancario (en ambos modos un fallo deja basura). Proveedor SIN lector nunca entra.
  const parseBloqueado = esLectorPdf && parseStatus === "error";
  // Distinción para el aviso UI: bloqueo por NO-PDF (A1, se subió una imagen) vs por fallo de parseo /
  // PDF corrupto. Cambia solo el texto del mensaje, no la dureza del bloqueo.
  const parseBloqueadoEsNoPdf =
    parseBloqueado && !!extArchivo && !(extArchivo.type === "application/pdf" || /\.pdf$/i.test(extArchivo.name || ""));

  const isValid =
    !loading &&
    !!(proveedorIdFinal || (modoNuevoProv && nuevoProvNombre.trim())) &&
    !!extFecha &&
    !!extConcepto.trim() &&
    !!extCuentaER &&
    !!extArchivo &&
    (isModoBancario || montoFinal > 0) &&
    !monedaMismatchBancario &&
    !parseBloqueado;

  // Cambio de archivo. El MISMO File que se elige se guarda en Storage (archivo_url) y —si el
  // proveedor tiene lector de PDF registrado y es un PDF— se lee con su parser para pre-llenar los
  // campos. NO hay doble subida: se lee el File en memoria (arrayBuffer), no se sube. Los campos
  // pre-llenados quedan editables (el parser acelera, no bloquea). Si el PDF no se reconoce, se
  // avisa por toast y el form sigue editable; el archivo se conserva y se sube igual.
  const handleArchivoChange = async (file) => {
    setExtArchivo(file);
    // Resetear la moneda del PDF ante cualquier archivo nuevo — evita que una lectura previa (USD)
    // deje bloqueado el guard P4 si el nuevo archivo no se parsea (imagen, PDF no reconocido, error).
    setMonedaParser(null);
    // P5: reset del estado de lectura ANTES de los early-returns. Sin archivo → 'idle' (arranque limpio,
    // no bloquea). Con proveedor sin lector → 'idle' (nunca aplica el guard).
    setParseStatus("idle");
    if (!file || !esLectorPdf) return;
    const esPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
    // A1: un proveedor con lector OBLIGA PDF. Subir un no-PDF (imagen) no permite lectura estructurada →
    // se marca 'error' para bloquear (los campos quedarían en defaults peligrosos). Distinto de "aún no
    // subió nada" (sin archivo → 'idle', arriba).
    if (!esPdf) {
      setParseStatus("error");
      return;
    }
    setParseandoPdf(true);
    try {
      // El parser registrado devuelve el shape transversal { folio, fecha, periodo, concepto, monto,
      // moneda, unidades }. Se mapea a los MISMOS campos de form (el INSERT no cambia). file.name lo
      // usa Screenfeed (su folio sale del nombre del archivo).
      const texto = await extraerTextoPdf(file);
      const r = configLector.parsear(texto, file.name);
      if (r.periodo) setExtPeriodoMes(defaultPeriodoMes(r.periodo));
      if (r.fecha) setExtFecha(r.fecha);
      if (r.folio) setExtNumeroDoc(r.folio);
      setExtLicencias(r.unidades != null ? String(r.unidades) : "");
      // La moneda del PDF se guarda SIEMPRE (también en bancario) para el guard P4 — no pisa el
      // espejado del monto/moneda de la factura, solo alimenta la comparación contra la del banco.
      setMonedaParser(r.moneda || null);
      // Monto/moneda: SOLO en standalone. En modo bancario se espejan del banco (fuente de verdad
      // ahí) — el PDF no los pisa.
      if (!isModoBancario) {
        if (r.monto != null) setExtMonto(String(r.monto));
        if (r.moneda) setExtMoneda(r.moneda);
      }
      // Cuenta ER desde el default del proveedor (Frente 1 pobló los 7). OnSign 18 = software_ds →
      // mismo resultado que antes. Queda visible y editable en el dropdown.
      const provSel = proveedores.find((p) => p.id === Number(proveedorIdFinal));
      if (provSel?.cuenta_er_default) setExtCuentaER(provSel.cuenta_er_default);
      // Concepto por molde: "{Nombre} {AAAA}/{MM} · {N} {unidad|unidades}". Sin unidades (o proveedor
      // sin etiqueta uni) → solo "{Nombre} {AAAA}/{MM}". anio/mm salen de `periodo` ("YYYY-MM-01").
      const [anio, mm] = String(r.periodo).split("-");
      const base = `${configLector.nombre} ${anio}/${mm}`;
      const concepto =
        r.unidades != null && configLector.uni
          ? `${base} · ${r.unidades} ${r.unidades === 1 ? configLector.uni.s : configLector.uni.p}`
          : base;
      setExtConcepto(concepto);
      setParseStatus("ok");
      toast.success("Datos leídos del PDF");
    } catch {
      // P5 opción A: el parser lanzó (o el PDF es corrupto / sin texto). Ya NO se ofrece completar a
      // mano — se bloquea el guardado (parseBloqueado). El aviso de bloqueo lo muestra la UI.
      setParseStatus("error");
      toast.error("No se pudo leer la factura, contactá a soporte");
    } finally {
      setParseandoPdf(false);
    }
  };

  const handleSubmit = async () => {
    if (!proveedorIdFinal && !modoNuevoProv) return toast.error("Seleccioná un proveedor o creá uno nuevo");
    if (modoNuevoProv && !nuevoProvNombre.trim()) return toast.error("Ingresá el nombre del nuevo proveedor");
    if (!extFecha) return toast.error("Ingresá la fecha del documento");
    if (!extConcepto.trim()) return toast.error("Ingresá el concepto");
    if (!extCuentaER) return toast.error("Seleccioná la cuenta del Estado de Resultados");
    if (!extArchivo) return toast.error("Subí el documento (PDF/imagen)");
    if (!isModoBancario && montoFinal <= 0) return toast.error("Ingresá el monto");
    // Red dura P5 (opción A + A1): proveedor con lector cuyo PDF no se pudo leer (parser falló, PDF
    // corrupto, o se subió un no-PDF) → no se guarda (cubre cualquier camino que esquive el disabled).
    if (parseBloqueado) {
      const esPdfActual = extArchivo.type === "application/pdf" || (extArchivo.name && /\.pdf$/i.test(extArchivo.name));
      return toast.error(
        esPdfActual
          ? "No se pudo leer la factura, contactá a soporte"
          : "Este proveedor requiere el PDF de la factura (no una imagen)"
      );
    }
    // Red dura P4: bloquear "Crear nueva" en bancario cuando la moneda del PDF ≠ la del banco (cubre
    // cualquier camino que esquive el disabled del botón). El cross-currency legítimo va por standalone.
    if (monedaMismatchBancario)
      return toast.error(
        `La factura está en ${monedaParser} pero el movimiento bancario es ${monedaInitial}. ` +
          "Cargá esta factura desde Estado de Cuenta y luego vinculala al pago."
      );
    setLoading(true);
    try {
      let provId = proveedorIdFinal;
      let provNombre = proveedores.find((p) => p.id === provId)?.nombre || proveedorLockedNombre || "";
      if (modoNuevoProv) {
        const nuevo = await crearProveedor({
          pais,
          nombre: nuevoProvNombre.trim(),
          rut_ruc: nuevoProvRut.trim() || null,
          es_extranjero: true,
          habilitado: true,
          cuenta_er_default: extCuentaER,
          moneda_recurrente: monedaFinal,
        });
        // Guard anti-duplicado: si ya existe uno con ese RUT, usarlo en vez de crear.
        const entidadProv = nuevo?.yaExiste ? nuevo.proveedor : nuevo;
        if (!entidadProv?.id) throw new Error("No se pudo crear el proveedor");
        if (nuevo?.yaExiste) {
          toast(
            `Ya existe un proveedor con RUT ${entidadProv.rut_ruc || ""}: ${entidadProv.nombre}. Se usó el existente.`
          );
        }
        provId = entidadProv.id;
        provNombre = entidadProv.nombre;
        onProveedorCreado?.(entidadProv);
      }

      // Guard anti-duplicado de folio (P1 / patrón 10.145): si hay folio, verificar en DB que este
      // proveedor no tenga ya una factura con ese numero_documento (respalda el índice único parcial
      // de Fase 1). Corre ANTES de subir el archivo/insertar → mensaje claro sin dejar storage
      // huérfano. Se saltea para cargas SIN folio (null → fuera del guard, igual que el índice parcial).
      const folioTrim = extNumeroDoc.trim();
      if (folioTrim) {
        const { data: dupFolio, error: dupErr } = await client
          .from("facturas_externas")
          .select("id, fecha_emision")
          .eq("pais", pais)
          .eq("proveedor_id", Number(provId))
          .eq("numero_documento", folioTrim)
          .limit(1)
          .maybeSingle();
        if (dupErr) throw dupErr;
        if (dupFolio) {
          toast.error(
            `Ya existe una factura de ${provNombre} con folio ${folioTrim} (cargada el ${dupFolio.fecha_emision})`
          );
          return;
        }
      }

      // Candado 2 (P6 / regla 9.41): aviso BLANDO por proveedor + mismo periodo_mes con folio distinto
      // (posible duplicado del mismo mes con otro folio). Solo aplica a recurrentes (periodo_mes se puebla
      // solo si esRecurrente, regla 10.46) — los one-off no tienen período que comparar. Distinto del
      // Candado 1 (folio idéntico = bloqueo duro): acá el mismo mes con otro folio puede ser legítimo, así
      // que se avisa con window.confirm (patrón 10.79) y se deja continuar. Corre ANTES de subir el
      // archivo/insertar (no deja storage huérfano). Comparación por proveedor_id (ID, regla 10.7).
      if (esRecurrente) {
        const periodoStr = periodoMesToString(extPeriodoMes);
        if (periodoStr) {
          let queryDupPeriodo = client
            .from("facturas_externas")
            .select("id, numero_documento, fecha_emision")
            .eq("pais", pais)
            .eq("proveedor_id", Number(provId))
            .eq("periodo_mes", periodoStr);
          // El folio idéntico ya lo bloquea el Candado 1 → excluirlo para no doble-avisar. Sin folio, no
          // se excluye nada (cualquier factura del mismo período dispara el aviso).
          if (folioTrim) queryDupPeriodo = queryDupPeriodo.neq("numero_documento", folioTrim);
          const { data: dupPeriodo, error: dupPerErr } = await queryDupPeriodo.limit(1).maybeSingle();
          if (dupPerErr) throw dupPerErr;
          if (dupPeriodo) {
            const periodoLabel = `${MESES_LABELS[extPeriodoMes.getMonth()]} ${extPeriodoMes.getFullYear()}`;
            const folioPrevio = dupPeriodo.numero_documento ? ` (folio ${dupPeriodo.numero_documento})` : "";
            const seguir = window.confirm(
              `Ya existe una factura de ${provNombre} para el período ${periodoLabel}${folioPrevio}. ¿Cargar igual?`
            );
            if (!seguir) return;
          }
        }
      }

      const archivoUrl = await subirArchivoFactura(extArchivo);

      const { data: fe, error: feErr } = await client
        .from("facturas_externas")
        .insert({
          pais,
          proveedor_id: Number(provId),
          proveedor_nombre: provNombre,
          concepto: extConcepto.trim(),
          numero_documento: folioTrim || null,
          fecha_emision: extFecha,
          monto_neto: montoFinal,
          monto_impuesto: 0,
          monto_total: montoFinal,
          moneda: monedaFinal,
          cuenta_er: extCuentaER,
          archivo_url: archivoUrl,
          cantidad_unidades: extLicencias.trim() ? Number(extLicencias) : null,
          periodo_mes: esRecurrente ? periodoMesToString(extPeriodoMes) : null,
          estado: isModoBancario ? "pagada" : "pendiente",
          movimiento_bancario_id: isModoBancario ? movimiento.id : null,
          registrado_por: usuarioId || null,
        })
        .select()
        .single();
      if (feErr) throw feErr;

      if (isModoBancario) {
        // Converger persistencia con "Vincular existente": fila puente en MBF. Sin esto, al
        // desvincular cae a Sección B (handleDesvincularCaja) que no libera la factura → huérfano.
        const { error: mbfError } = await client.from("movimiento_bancario_facturas").insert({
          movimiento_bancario_id: movimiento.id,
          factura_tipo: "factura_externa",
          factura_id: fe.id,
          monto: montoFinal,
        });
        if (mbfError) {
          // Rollback: borrar la factura recién creada para no dejar huérfana (no hay transacción).
          await client.from("facturas_externas").delete().eq("id", fe.id);
          throw mbfError;
        }

        await actualizarMatch(movimiento.id, {
          match_entidad_tipo: "proveedor",
          match_entidad_id: Number(provId),
          match_entidad_nombre: provNombre,
          match_factura_tipo: "factura_externa",
          match_factura_id: fe.id,
          match_confianza: 1,
          match_razon: "factura_externa_manual",
        });

        const nuevoCaja = await crearMovimiento({
          pais,
          tipo: "pago",
          fecha: movimiento.fecha,
          monto: montoFinal,
          monto_neto: montoFinal,
          monto_impuesto: 0,
          moneda: monedaFinal,
          estado: "confirmado",
          descripcion: `${provNombre} | ${extConcepto.trim()}`,
          entidad_tipo: "proveedor",
          entidad_id: Number(provId),
          entidad_nombre: provNombre,
          categoria_pago: "pago_proveedor",
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (!nuevoCaja?.id) throw new Error("No se pudo crear el movimiento en caja");

        await confirmarMovimiento(movimiento.id, {
          movimiento_caja_id: nuevoCaja.id,
          procesado_por: usuarioId || null,
        });

        toast.success("Factura externa registrada y movimiento confirmado");
        onSuccess?.({
          id: movimiento.id,
          estado: "procesado",
          movimiento_caja_id: nuevoCaja.id,
          movimiento_caja: { categoria_pago: "pago_proveedor" },
          match_factura_tipo: "factura_externa",
          match_entidad_nombre: provNombre,
          factura_externa: [{ numero_documento: extNumeroDoc.trim() || null }],
          // Update optimista del Set de dedup en FlujoCajaV2 (mismo flag que "Vincular existente").
          _externasVinculadas: [fe.id],
        });
      } else {
        toast.success("Factura externa registrada");
        onFacturaCreada?.(fe);
      }
      onClose?.();
    } catch (err) {
      // Red de seguridad: si la DB rechaza por el índice único parcial de folio (23505), traducir a
      // un mensaje humano — cubre dos cargas casi-simultáneas que el pre-check en memoria no ve.
      if (err?.code === "23505") {
        toast.error("Esta factura ya está cargada (folio duplicado para este proveedor)");
      } else {
        toast.error("Error al registrar: " + (err.message || ""));
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    // state
    proveedorId,
    setProveedorId,
    modoNuevoProv,
    setModoNuevoProv,
    nuevoProvNombre,
    setNuevoProvNombre,
    nuevoProvRut,
    setNuevoProvRut,
    extFecha,
    setExtFecha,
    extPeriodoMes,
    setExtPeriodoMes,
    extNumeroDoc,
    setExtNumeroDoc,
    extConcepto,
    setExtConcepto,
    extCuentaER,
    setExtCuentaER,
    extArchivo,
    setExtArchivo,
    extMonto,
    setExtMonto,
    extMoneda,
    setExtMoneda,
    extLicencias,
    setExtLicencias,
    // computed
    isModoBancario,
    proveedorIdLocked,
    proveedorLockedNombre,
    monedaFinal,
    monedaParser,
    monedaBanco: monedaInitial,
    monedaMismatchBancario,
    parseBloqueado,
    parseBloqueadoEsNoPdf,
    esRecurrente,
    esLectorPdf,
    parseandoPdf,
    isValid,
    loading,
    // actions
    handleSubmit,
    handleArchivoChange,
  };
};

// ─── Form (UI puro, sin Modal wrapper) ───────────────────────
export const FacturaExternaForm = ({ form, pais, proveedores }) => {
  const monedaLocal = pais === "Chile" ? "CLP" : "PEN";

  // Bloque de subir el documento. Si el proveedor tiene lector de PDF, va ARRIBA y protagónico
  // (destacado con recuadro), porque dispara la lectura automática que llena todo lo de abajo. Para
  // el resto va al final, sin énfasis.
  const documentoBlock = (
    <div
      style={
        form.esLectorPdf
          ? { marginBottom: 16, padding: 14, border: "2px solid #2980b9", borderRadius: 8, background: "#eaf4fb" }
          : { marginBottom: 8 }
      }
    >
      <div
        style={{
          fontSize: form.esLectorPdf ? "0.9rem" : "0.78rem",
          fontWeight: 700,
          marginBottom: form.esLectorPdf ? 8 : 4,
          color: form.esLectorPdf ? "#1f618d" : "inherit",
        }}
      >
        {form.esLectorPdf ? "1 · Subí el PDF de la factura" : "Documento (PDF o imagen)"}
      </div>
      {form.esLectorPdf && (
        <div style={{ fontSize: "0.76rem", color: "#2980b9", marginBottom: 8 }}>
          📄 Se leen automáticamente período, N° de recibo, licencias
          {form.isModoBancario ? " y fecha" : ", monto y fecha"}, concepto y cuenta. Los campos de abajo se completan
          solos — revisalos y confirmá.
        </div>
      )}
      <input
        type="file"
        className="form-control form-control-sm"
        accept="application/pdf,image/*"
        onChange={(e) => form.handleArchivoChange(e.target.files?.[0] || null)}
        style={{ fontSize: "0.82rem" }}
      />
      {form.parseandoPdf && <div style={{ fontSize: "0.72rem", color: "#2980b9", marginTop: 4 }}>Leyendo PDF…</div>}
      {form.extArchivo && (
        <div style={{ fontSize: "0.72rem", color: "#27ae60", marginTop: 4 }}>✓ {form.extArchivo.name}</div>
      )}
    </div>
  );

  return (
    <>
      {form.proveedorIdLocked ? (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Proveedor</div>
          <div
            style={{
              padding: "6px 10px",
              border: "1px solid #dee2e6",
              borderRadius: 4,
              background: "#f8f9fa",
              fontSize: "0.85rem",
            }}
          >
            {form.proveedorLockedNombre}
          </div>
        </div>
      ) : (
        <ProveedorSelector
          value={form.proveedorId}
          onChange={(p) => {
            form.setProveedorId(p.id);
            if (p.cuenta_er_default && !form.extCuentaER) form.setExtCuentaER(p.cuenta_er_default);
          }}
          filter={(p) => p.es_extranjero}
          proveedores={proveedores}
          onCrearNuevo={() => form.setModoNuevoProv(true)}
          modoNuevo={form.modoNuevoProv}
          onModoNuevoChange={form.setModoNuevoProv}
          nuevoNombre={form.nuevoProvNombre}
          onNuevoNombreChange={form.setNuevoProvNombre}
          nuevoRut={form.nuevoProvRut}
          onNuevoRutChange={form.setNuevoProvRut}
          hintCreacion={`Se marca como extranjero (es_extranjero=true) y se registra con moneda ${form.monedaFinal}.`}
        />
      )}

      {form.esLectorPdf && documentoBlock}

      {form.parseBloqueado && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #e74c3c",
            borderRadius: 6,
            background: "#fdedec",
            fontSize: "0.8rem",
            color: "#c0392b",
          }}
        >
          {form.parseBloqueadoEsNoPdf ? (
            <>
              ⚠️ Este proveedor requiere el <strong>PDF</strong> de la factura (no una imagen). Subí el PDF para poder
              leer los datos.
            </>
          ) : (
            <>⚠️ No se pudo leer la factura, contactá a soporte.</>
          )}
        </div>
      )}

      {form.monedaMismatchBancario && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #e74c3c",
            borderRadius: 6,
            background: "#fdedec",
            fontSize: "0.8rem",
            color: "#c0392b",
          }}
        >
          ⚠️ La factura está en <strong>{form.monedaParser}</strong> pero el movimiento bancario es{" "}
          <strong>{form.monedaBanco}</strong>. Cargá esta factura desde <strong>Estado de Cuenta</strong> y luego
          vinculala al pago.
        </div>
      )}

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Fecha documento</div>
        <input
          type="date"
          className="form-control form-control-sm"
          value={form.extFecha}
          onChange={(e) => form.setExtFecha(e.target.value)}
          style={{ fontSize: "0.82rem" }}
        />
      </div>

      {form.esRecurrente && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Período</div>
          <div style={{ display: "flex", gap: 8 }}>
            <select
              className="form-select form-select-sm"
              value={form.extPeriodoMes.getMonth()}
              onChange={(e) =>
                form.setExtPeriodoMes(new Date(form.extPeriodoMes.getFullYear(), Number(e.target.value), 1))
              }
              style={{ fontSize: "0.82rem", flex: 1 }}
            >
              {MESES_LABELS.map((label, i) => (
                <option key={i} value={i}>
                  {label}
                </option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              value={form.extPeriodoMes.getFullYear()}
              onChange={(e) =>
                form.setExtPeriodoMes(new Date(Number(e.target.value), form.extPeriodoMes.getMonth(), 1))
              }
              style={{ fontSize: "0.82rem", width: 100 }}
            >
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
              <option value={2027}>2027</option>
            </select>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>N° documento (opcional)</div>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Ej: 3YK4QC1M-0005"
          value={form.extNumeroDoc}
          onChange={(e) => form.setExtNumeroDoc(e.target.value)}
          style={{ fontSize: "0.82rem" }}
        />
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Licencias / unidades (opcional)</div>
        <input
          type="text"
          inputMode="numeric"
          className="form-control form-control-sm"
          placeholder="Ej: 187"
          value={form.extLicencias}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "" || /^\d+$/.test(v)) form.setExtLicencias(v);
          }}
          style={{ fontSize: "0.82rem" }}
        />
      </div>

      {!form.isModoBancario && (
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <div style={{ flex: 2 }}>
            <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Monto</div>
            <input
              type="text"
              inputMode="decimal"
              className="form-control form-control-sm"
              placeholder="0.00"
              value={form.extMonto}
              onChange={(e) => {
                const normalized = e.target.value.replace(",", ".");
                if (normalized === "" || /^\d*\.?\d*$/.test(normalized)) {
                  form.setExtMonto(normalized);
                }
              }}
              style={{ fontSize: "0.82rem" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Moneda</div>
            <select
              className="form-select form-select-sm"
              value={form.extMoneda}
              onChange={(e) => form.setExtMoneda(e.target.value)}
              style={{ fontSize: "0.82rem" }}
            >
              <option value="USD">USD</option>
              <option value={monedaLocal}>{monedaLocal}</option>
            </select>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Concepto</div>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Ej: Team plan - Standard Apr 18–May 18, 2026"
          value={form.extConcepto}
          onChange={(e) => form.setExtConcepto(e.target.value)}
          style={{ fontSize: "0.82rem" }}
        />
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Cuenta Estado de Resultados</div>
        <select
          className="form-select form-select-sm"
          value={form.extCuentaER}
          onChange={(e) => form.setExtCuentaER(e.target.value)}
          style={{ fontSize: "0.82rem" }}
        >
          <option value="">Seleccionar cuenta…</option>
          {["Costos Fijos", "Costos Variables"].map((grupo) => (
            <optgroup key={grupo} label={grupo}>
              {CUENTAS_ER.filter((c) => c.grupo === grupo).map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {!form.esLectorPdf && documentoBlock}
    </>
  );
};

// ─── Modal wrapper (full standalone) ─────────────────────────
const FacturaExternaModal = (props) => {
  const form = useFacturaExternaForm(props);
  if (!props.show) return null;

  return (
    <Modal show={props.show} onHide={props.onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>Cargar factura externa</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FacturaExternaForm form={form} pais={props.pais} proveedores={props.proveedores} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={props.onClose}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={form.handleSubmit} disabled={!form.isValid}>
          {form.loading ? "Registrando..." : "Confirmar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FacturaExternaModal;
